import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../../context/NotificationContext";
import { Card, Table, Input, Button, Space, Tag } from "antd";
import { PlusOutlined, EditOutlined } from "@ant-design/icons";
import videoService from "../../services/videoService";

const { Search } = Input;

const CategoryList = () => {
  const navigate = useNavigate();
  const notify = useNotify();

  // State management
  const [videoCategories, setVideoCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) =>
      `${range[0]}-${range[1]} of ${total} categories`,
  });

  // Filters state
  const [filters, setFilters] = useState({
    searchText: "",
  });

  // Table columns configuration
  const columns = [
    {
      title: "S.No",
      key: "sno",
      width: 80,
      render: (text, record, index) => index + 1,
    },
    {
      title: "Order",
      dataIndex: "order",
      key: "order",
      width: 100,
      sorter: (a, b) => a.order - b.order,
    },
    {
      title: "ID",
      dataIndex: "vc_id",
      key: "vc_id",
      width: 100,
      sorter: (a, b) => a.vc_id - b.vc_id,
    },
    {
      title: "Category Name",
      dataIndex: "vc_name",
      key: "vc_name",
      sorter: (a, b) => {
        const nameA = a.vc_name || '';
        const nameB = b.vc_name || '';
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: "Status",
      dataIndex: "vc_is_active",
      key: "vc_is_active",
      width: 120,
      render: (text) => (
        <Tag color={text === "Y" ? "green" : "red"}>
          {text === "Y" ? "Active" : "Inactive"}
        </Tag>
      ),
      filters: [
        { text: "Active", value: "Y" },
        { text: "Inactive", value: "N" },
      ],
      onFilter: (value, record) => record.vc_is_active === value,
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      fixed: "right",
      render: (text, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => navigate(`/categories/category/edit/${record.vc_id}`)}
            size="small"
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  // Fetch video categories data
  const getAllVideoCategory = async (params = {}) => {
    try {
      setLoading(true);
      const queryParams = {
        perPage: pagination.pageSize,
        currentPage: pagination.current,
        searchText: filters.searchText,
        ...params,
      };

      console.log("Fetching video categories with params:", queryParams);
      const response = await videoService.getAllVideoCategory(queryParams);
      console.log("API Response:", response);

      const { data } = response;

      if (data && data.status) {
        setVideoCategories(data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: data.data?.length || 0,
        }));
      } else {
        console.log("API returned no data or status false:", data);
        setVideoCategories([]);
      }
    } catch (error) {
      console.error("Error fetching video categories:", error);
      notify.error({ message: "Failed to fetch video categories" });
      setVideoCategories([]);
    } finally {
      setLoading(false);
    }
  };

  // Update video category order

  // Handle search
  const handleSearch = (value) => {
    setFilters((prev) => ({ ...prev, searchText: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    getAllVideoCategory({
      searchText: value,
      perPage: pagination.pageSize,
      currentPage: 1,
    });
  };

  // Handle table change (pagination, sorting)
  const handleTableChange = (newPagination, tableFilters, sorter) => {
    const { current, pageSize } = newPagination;

    if (current !== pagination.current || pageSize !== pagination.pageSize) {
      setPagination((prev) => ({
        ...prev,
        current,
        pageSize,
      }));

      getAllVideoCategory({
        searchText: filters.searchText,
        perPage: pageSize,
        currentPage: current,
      });
    }
  };

  // Handle add new category
  const handleAddNew = () => {
    navigate("/categories/category/add");
  };

  // Load data on component mount
  useEffect(() => {
    getAllVideoCategory();
  }, []);

  // Header content with user info

  return (
      <Card>
        {/* Table Operations */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
            flexWrap: "wrap",
            gap: "8px",
          }}
        >
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddNew}
              style={{ marginRight: "15px" }}
            >
              Add Category
            </Button>
          </div>

          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <Search
              placeholder="Search categories..."
              style={{ width: 300 }}
              value={filters.searchText}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, searchText: e.target.value }))
              }
              onSearch={handleSearch}
              allowClear
            />
          </div>
        </div>

        {/* Data Table */}
        <Table
          columns={columns}
          dataSource={videoCategories}
          rowKey="vc_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 600 }}
          size="middle"
        />
      </Card>
  );
};

export default CategoryList;
